import { replaceTokensFlipped, withProps } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import { lensPath, lensProp, over, path, pick, when } from 'ramda'
import { isNotNil } from 'ramda-adjunct'
import map from 'ramda/src/map'
import pipe from 'ramda/src/pipe'
import React from 'react'
import SiteFooterMinimal from '../../Site/footers/SiteFooterMinimal'
import SiteHeaderPortal from '../../Site/headers/SiteHeaderPortal'
import DefaultPage from '../DefaultPage'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const PRIMARY_NAV = [
  {
    title: `Knowledge base`,
    body: `Download advice from our sleep experts`,
    image: `/images/portal-nav-icon-1.svg`,
    url: `/portal/knowledge-base/`,
  },
  {
    title: `Test your thinking`,
    body: `What do you know about sleep?`,
    image: `/images/portal-nav-icon-3.svg`,
    url: `/portal/what-do-you-know-about-sleep/intro/`,
  },
  {
    title: `Check your sleep`,
    body: `Are you getting enough sleep?`,
    image: `/images/portal-nav-icon-2.svg`,
    url: `/portal/are-you-getting-enough-sleep/intro/`,
  },
  {
    title: `Could you have insomnia?`,
    body: `Find out how Sleepstation can help you sleep better.`,
    image: `/images/portal-nav-icon-4.svg`,
    url: `/portal/could-you-have-insomnia/`,
  },
  {
    title: `Expert guidance`,
    body: `Support and guidance from our sleep team`,
    image: `/images/portal-nav-icon-10.svg`,
    url: `/portal/expert-guidance/`,
    isHighlighted: true,
  },
]

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const getPrimaryNav = (getPath) =>
  map(over(lensProp(`url`), getPath), PRIMARY_NAV)

const pathMetadataTitle = lensPath([`page`, `metadata`, `title`])

const hasAuthConfig = pipe(path([`page`, `authenticationConfig`]), isNotNil)

const pathPortalAuthenticationConfigRedirectUrlInvalid = lensPath([
  `page`,
  `authenticationConfig`,
  `redirectUrlInvalid`,
])

const pathPortalAuthenticationConfigRedirectUrlValid = lensPath([
  `page`,
  `authenticationConfig`,
  `redirectUrlValid`,
])

const replaceInvalidUrlRedirectTokens = (tokens) =>
  over(
    pathPortalAuthenticationConfigRedirectUrlInvalid,
    when(isNotNil, replaceTokensFlipped(tokens))
  )

const replaceValidUrlRedirectTokens = (tokens) =>
  over(
    pathPortalAuthenticationConfigRedirectUrlValid,
    when(isNotNil, replaceTokensFlipped(tokens))
  )

const replaceMetaTitleTokens = (tokens) =>
  over(pathMetadataTitle, replaceTokensFlipped(tokens))

const replaceRedirectTokens = (tokens) =>
  pipe(
    replaceInvalidUrlRedirectTokens(tokens),
    replaceValidUrlRedirectTokens(tokens)
  )

const transformPageData = (data) => {
  const tokens = pick([`fullName`, `namespace`], data.portal)
  return pipe(
    replaceMetaTitleTokens(tokens),
    when(hasAuthConfig, replaceRedirectTokens(tokens))
  )(data)
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const PortalPage = ({ data, showHeader, shouldOffsetContent, ...rest }) => {
  const { portal } = data

  const homeUrl = portal.helpers.getPath(`/portal/dashboard/`)
  const navItems = getPrimaryNav(portal.helpers.getPath)

  const termsAndConditionsUrl = portal.helpers.getPath(`/portal/terms/`)
  const privacyUrl = portal.helpers.getPath(`/portal/privacy/`)
  const informationStandardUrl = portal.helpers.getPath(
    `/portal/the-information-standard`
  )

  // Transform data within the page that needs to have portal-specific data
  // included.
  const transformedData = transformPageData(data)

  return (
    <DefaultPage
      SiteFooter={SiteFooterMinimal}
      SiteHeader={withProps(SiteHeaderPortal, { homeUrl, navItems })}
      termsAndConditionsPath={termsAndConditionsUrl}
      privacyPolicyPath={privacyUrl}
      informationStandardPath={informationStandardUrl}
      data={transformedData}
      showHeader={showHeader}
      shouldOffsetContent={shouldOffsetContent}
      {...rest}
    />
  )
}

PortalPage.propTypes = {
  data: PropTypes.object.isRequired,
  shouldOffsetContent: PropTypes.bool,
  showHeader: PropTypes.bool,
}

PortalPage.defaultProps = {
  shouldOffsetContent: false,
  showHeader: false,
}

export default PortalPage
